import React, { useState, useEffect } from 'react';
import MainLayout from '../layout/MainLayout';
import planApi from '../../services/planApi';
import config from '../../config';
import { Lock, AlertCircle, ChevronDown, MessageSquare } from 'lucide-react';
import './admin-ratings.scss';

interface Sess {
  n: number;
  month_idx: number | null;
  title: string;
  mentee_rating: number | null;
  mentor_rating: number | null;
  mentee_comment: string | null;
  mentor_comment: string | null;
}
interface Dupla {
  relationship_id: number;
  mentee: string; mentor: string;
  mentee_image: string | null; mentor_image: string | null;
  mentee_avg: number | null; mentor_avg: number | null;
  total: number; mentee_rated: number; mentor_rated: number;
  at_risk: boolean;
  sessions: Sess[];
}
interface Data { risk_count: number; duplas: Dupla[]; }

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${config.api.baseUrl.replace(/\/api\/?$/, '')}/${imagePath}`;
};
const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const eyebrowDate = () => {
  const d = new Date();
  const dia = d.toLocaleDateString('es', { weekday: 'long' });
  const mes = d.toLocaleDateString('es', { month: 'short' }).replace('.', '');
  return `${dia} ${d.getDate()} de ${mes} · Administrador`.toUpperCase();
};

const Stars: React.FC<{ n: number | null }> = ({ n }) => (
  <span className="rt-stars">
    {[1, 2, 3, 4, 5].map(i => (
      <span key={i} className={n && i <= n ? 'on' : 'off'}>★</span>
    ))}
  </span>
);

const Avatars: React.FC<{ d: Dupla }> = ({ d }) => {
  const me = getImageUrl(d.mentee_image);
  const mt = getImageUrl(d.mentor_image);
  return (
    <div className="rt-avatars">
      <span className="rt-avatar rt-avatar--back">{mt ? <img src={mt} alt={d.mentor} /> : <span className="rt-avatar__ini">{initials(d.mentor)}</span>}</span>
      <span className="rt-avatar rt-avatar--front">{me ? <img src={me} alt={d.mentee} /> : <span className="rt-avatar__ini">{initials(d.mentee)}</span>}</span>
    </div>
  );
};

const DuplaCard: React.FC<{ d: Dupla; defaultOpen?: boolean }> = ({ d, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [openComment, setOpenComment] = useState<number | null>(null);
  const fmt = (v: number | null) => (v === null ? '—' : String(v));
  return (
    <div className={`rt-card ${d.at_risk ? 'is-risk' : ''} ${open ? 'is-open' : ''}`}>
      <button className="rt-card__head" onClick={() => setOpen(o => !o)}>
        <div className="rt-card__dupla">
          <Avatars d={d} />
          <div className="rt-card__names">
            <span className="rt-card__mentee">{d.mentee}</span>
            <span className="rt-card__mentor">↪ {d.mentor}</span>
          </div>
        </div>
        <div className="rt-card__avgs">
          <span className="rt-avg"><em>{fmt(d.mentee_avg)}</em>★ <span className="rt-avg__lbl">mentee</span></span>
          <span className="rt-avg"><em>{fmt(d.mentor_avg)}</em>★ <span className="rt-avg__lbl">mentor</span></span>
          <ChevronDown size={20} className="rt-card__chev" />
        </div>
      </button>

      {open && (
        <div className="rt-card__body">
          {d.sessions.length === 0 ? (
            <p className="rt-empty">Esta dupla aún no tiene sesiones calificadas.</p>
          ) : (
            <>
              {d.sessions.map(s => (
                <div className="rt-sess" key={s.n}>
                  <div className="rt-sess__row">
                    <span className="rt-sess__label">Sesión {s.n}{s.month_idx ? ` · Mes ${s.month_idx}` : ''}</span>
                    <span className="rt-sess__side">
                      <span className="rt-tag rt-tag--mentee">Mentee</span> <Stars n={s.mentee_rating} />
                    </span>
                    <span className="rt-sess__side">
                      <span className="rt-tag rt-tag--mentor">Mentor</span> <Stars n={s.mentor_rating} />
                    </span>
                    {(s.mentee_comment || s.mentor_comment) ? (
                      <button className="rt-sess__cmt" onClick={() => setOpenComment(openComment === s.n ? null : s.n)}>
                        <MessageSquare size={14} /> Ver comentario
                      </button>
                    ) : (
                      <span className="rt-sess__cmt rt-sess__cmt--muted">Sin comentario</span>
                    )}
                  </div>
                  {openComment === s.n && (
                    <div className="rt-comments">
                      <div className="rt-comment">
                        <span className="rt-tag rt-tag--mentee">Mentee</span>
                        {s.mentee_comment ? <span className="rt-comment__txt">“{s.mentee_comment}”</span> : <span className="rt-comment__muted">Sin comentario</span>}
                      </div>
                      <div className="rt-comment">
                        <span className="rt-tag rt-tag--mentor">Mentor</span>
                        {s.mentor_comment ? <span className="rt-comment__txt">“{s.mentor_comment}”</span> : <span className="rt-comment__muted">Sin comentario</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="rt-card__foot">
                Promedio {fmt(d.mentee_avg)}★ mentee · {fmt(d.mentor_avg)}★ mentor · calificadas {d.mentee_rated}/{d.total} y {d.mentor_rated}/{d.total}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AdminRatings: React.FC = () => {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    planApi.getRatings()
      .then((res: Data) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <MainLayout>
      <div className="admin-ratings">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{eyebrowDate()}</div>
            <h1 className="heading-1">Calificaciones de <em>sesión</em></h1>
            <p className="rt-intro">Cómo califican mentor y mentee. Vista confidencial, exclusiva del administrador.</p>
          </div>
        </div>

        <div className="rt-banner rt-banner--info">
          <Lock size={18} />
          <span><strong>Vista confidencial.</strong> El mentor no ve cómo lo califica el mentee, ni viceversa.</span>
        </div>

        {loading ? (
          <div className="rt-loading">Cargando calificaciones…</div>
        ) : !data ? (
          <div className="rt-loading">No se pudieron cargar las calificaciones.</div>
        ) : (
          <>
            {data.risk_count > 0 && (
              <div className="rt-banner rt-banner--risk">
                <AlertCircle size={18} />
                <span>
                  <strong>{data.risk_count} {data.risk_count === 1 ? 'dupla con señal de riesgo de match' : 'duplas con señal de riesgo de match'}.</strong>{' '}
                  Promedio del mentee en 2★ o menos, o brecha grande entre mentee y mentor.
                </span>
              </div>
            )}

            <div className="rt-cards">
              {data.duplas.length === 0 ? (
                <p className="rt-empty rt-empty--flat">No hay duplas activas.</p>
              ) : data.duplas.map((d, i) => (
                <DuplaCard key={d.relationship_id} d={d} defaultOpen={i === 0} />
              ))}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminRatings;
